/* eslint-disable */
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import dialogStore from '@/store/dialog.store';
import { isNativePlatform, getPlatform, resolveAppUrl, withTimeout } from '@/utils/capacitor';

let isChecking = false;
let isDialogOpen = false;
let periodicTimer = null;

// Lazy resolver for i18n to prevent circular dependency temporal dead zones during boot
function getTranslator() {
  try {
    const i18nModule = require('@/i18n');
    const i18n = i18nModule.default || i18nModule;
    if (i18n && i18n.global) {
      return {
        t: (k, p) => (p !== undefined ? i18n.global.t(k, p) : i18n.global.t(k)),
        te: (k) => i18n.global.te(k)
      };
    }
  } catch (e) {}
  return {
    t: (k) => k,
    te: () => false
  };
}

/**
 * Parses a semantic version string into [major, minor, patch] integers.
 * Handles strings like "1.0.48", "v1.0.48", "1.0", "1.0.48-beta.1".
 */
export function parseSemver(v) {
  if (!v || typeof v !== 'string') return [0, 0, 0];
  const cleaned = v.trim().replace(/^[vV]/, '');
  const parts = cleaned.split(/[-+]/)[0].split('.');
  const major = parseInt(parts[0], 10) || 0;
  const minor = parseInt(parts[1], 10) || 0;
  const patch = parseInt(parts[2], 10) || 0;
  return [major, minor, patch];
}

/**
 * Compares two semantic version strings.
 * Returns:
 *   1 if v1 > v2
 *  -1 if v1 < v2
 *   0 if v1 === v2
 */
export function compareVersions(v1, v2) {
  const [maj1, min1, pat1] = parseSemver(v1);
  const [maj2, min2, pat2] = parseSemver(v2);

  if (maj1 !== maj2) return maj1 > maj2 ? 1 : -1;
  if (min1 !== min2) return min1 > min2 ? 1 : -1;
  if (pat1 !== pat2) return pat1 > pat2 ? 1 : -1;
  return 0;
}

export function isNewerVersion(currentVersion, remoteVersion) {
  return compareVersions(remoteVersion, currentVersion) > 0;
}

export function isOlderThanMin(currentVersion, minVersion) {
  if (!minVersion) return false;
  return compareVersions(currentVersion, minVersion) < 0;
}

/**
 * Retrieves current installed/running app version.
 * On native, queries App.getInfo().
 * Falls back to packaged /version.json.
 */
export async function getCurrentAppVersion() {
  let version = null;

  if (isNativePlatform()) {
    try {
      const info = await App.getInfo();
      if (info && info.version) {
        version = info.version;
      }
    } catch (e) {
      console.warn('[AppUpdate] App.getInfo() failed:', e);
    }
  }

  // Fallback to local version.json (generated at build time from package.json)
  if (!version || version === '1.0' || version === '1.0.0') {
    try {
      const res = await withTimeout(fetch(`/version.json?t=${Date.now()}`), 3000);
      if (res.ok) {
        const data = await res.json();
        if (data && data.version) {
          version = data.version;
        }
      }
    } catch (e) {
      // offline or not available
    }
  }

  return version || '1.0.0';
}

/**
 * Queries remote server for app update configuration.
 */
export async function fetchRemoteVersion() {
  const url = resolveAppUrl(`/api/app/version?t=${Date.now()}`);
  const res = await withTimeout(
    fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        'Accept': 'application/json'
      }
    }),
    8000
  );

  if (!res.ok) {
    throw new Error(`Failed to fetch version info: HTTP ${res.status}`);
  }

  return await res.json();
}

/**
 * Returns comprehensive version summary for UI displays (e.g. SideMenu footer).
 * Contains current running app version, native build code, commit hash,
 * and remote web system version.
 */
export async function getAppVersionSummary() {
  let appVersion = '1.1.2';
  let appBuild = '';
  let appCommit = '';
  let webVersion = '1.1.2';
  let webCommit = '';
  let webBuildTime = '';
  let releaseNotes = '';

  const isNative = isNativePlatform();

  if (isNative) {
    try {
      const info = await App.getInfo();
      if (info && info.version) {
        appVersion = info.version;
        appBuild = info.build || '';
      }
    } catch (e) {
      console.warn('[AppUpdate] App.getInfo failed:', e);
    }
  }

  // Get local client build info (commit / version if not native)
  try {
    const res = await withTimeout(fetch(`/version.json?t=${Date.now()}`), 2500);
    if (res.ok) {
      const data = await res.json();
      if (!isNative && data.version) {
        appVersion = data.version;
      }
      if (data.commit) appCommit = data.commit;
    }
  } catch (e) {}

  let targetVersion = appVersion;

  // Fetch remote version config
  try {
    const remote = await fetchRemoteVersion();
    if (remote) {
      const remoteStoreVersion = remote.latestVersion || appVersion;
      const remoteWebVersion = remote.webVersion || remote.latestVersion || appVersion;

      targetVersion = isNative ? remoteStoreVersion : remoteWebVersion;
      webVersion = targetVersion;

      if (remote.webCommit) webCommit = remote.webCommit;
      if (remote.webBuildTime) webBuildTime = remote.webBuildTime;
      if (remote.releaseNotes) releaseNotes = remote.releaseNotes;
    }
  } catch (e) {
    targetVersion = appVersion;
  }

  const hasUpdate = isNewerVersion(appVersion, targetVersion);

  return {
    appVersion,
    appBuild,
    appCommit,
    webVersion: targetVersion,
    webCommit,
    webBuildTime,
    releaseNotes,
    hasUpdate
  };
}

/**
 * Redirects user to native store (Google Play / Apple App Store) or web application.
 */
export async function redirectToStore(urls = {}) {
  const platform = getPlatform(); // 'android' | 'ios' | 'web'
  const defaultAndroid = 'market://details?id=app.triadmusic.live';
  const defaultAndroidWeb = 'https://play.google.com/store/apps/details?id=app.triadmusic.live';
  const defaultIos = 'itms-apps://itunes.apple.com/app/id6446261234';
  const defaultIosWeb = 'https://apps.apple.com/app/id6446261234';
  const defaultWeb = 'https://live.triadmusic.app';

  if (platform === 'android' && isNativePlatform()) {
    const marketUrl = (urls && urls.android) || defaultAndroid;
    const webUrl = (urls && urls.androidWeb) || defaultAndroidWeb;

    try {
      // Native market intent opens Google Play Store directly
      window.location.href = marketUrl;
      return;
    } catch (e) {
      console.warn('[AppUpdate] market:// direct intent navigation failed:', e);
    }
    try {
      await Browser.open({ url: webUrl });
      return;
    } catch (e) {
      console.warn('[AppUpdate] Browser.open failed:', e);
      window.open(webUrl, '_system');
    }
  } else if (platform === 'ios' && isNativePlatform()) {
    const appStoreUrl = (urls && urls.ios) || defaultIos;
    const webUrl = (urls && urls.iosWeb) || defaultIosWeb;

    try {
      window.location.href = appStoreUrl;
      return;
    } catch (e) {
      console.warn('[AppUpdate] itms-apps:// navigation failed:', e);
    }
    try {
      await Browser.open({ url: webUrl });
      return;
    } catch (e) {
      console.warn('[AppUpdate] Browser.open failed:', e);
      window.open(webUrl, '_system');
    }
  } else {
    // Web platform
    const targetUrl = (urls && urls.web) || defaultWeb;
    if (window.location && window.location.origin && targetUrl.startsWith(window.location.origin)) {
      window.location.reload(true);
    } else {
      window.location.href = targetUrl;
    }
  }
}

/**
 * Main update check method.
 * Checks version against remote server and triggers prompt when newer version is published.
 * @param {Object} options
 * @param {boolean} options.ignoreDismissed - Ignore dismissal check for this session
 */
export async function checkForAppUpdate(options = {}) {
  if (isChecking || isDialogOpen) {
    return { checked: false, reason: 'in_progress' };
  }

  isChecking = true;

  try {
    const [currentVersion, remoteConfig] = await Promise.all([
      getCurrentAppVersion(),
      fetchRemoteVersion()
    ]);

    const isNative = isNativePlatform();
    const latestVersion = isNative
      ? (remoteConfig.latestVersion || '1.0.0')
      : (remoteConfig.webVersion || remoteConfig.latestVersion || '1.0.0');
    const minVersion = remoteConfig.minVersion || '1.0.0';
    const mustForceUpdate = isNative && (remoteConfig.forceUpdate === true || isOlderThanMin(currentVersion, minVersion));
    const hasNewerVersion = isNewerVersion(currentVersion, latestVersion);

    if (!mustForceUpdate && !hasNewerVersion) {
      return { checked: true, hasUpdate: false, currentVersion, latestVersion };
    }

    // Check if dismissed in this session (only for optional/non-mandatory updates)
    const dismissedKey = 'triad_dismissed_update';
    if (!mustForceUpdate && !options.ignoreDismissed) {
      const previouslyDismissed = sessionStorage.getItem(dismissedKey);
      if (previouslyDismissed === latestVersion) {
        return { checked: true, hasUpdate: true, dismissed: true, currentVersion, latestVersion };
      }
    }

    const { t, te } = getTranslator();

    const title = mustForceUpdate
      ? (te('appUpdate.forceTitle') ? t('appUpdate.forceTitle') : 'Atualização Obrigatória')
      : (te('appUpdate.title') ? t('appUpdate.title') : 'Atualização Disponível');

    let message = mustForceUpdate
      ? (te('appUpdate.forceMessage') ? t('appUpdate.forceMessage', { version: latestVersion }) : `Uma nova versão (${latestVersion}) é necessária para continuar utilizando o aplicativo. Por favor, atualize pela loja de aplicativos.`)
      : (te('appUpdate.message') ? t('appUpdate.message', { version: latestVersion }) : `Uma nova versão (${latestVersion}) está disponível na loja. Deseja atualizar agora?`);

    if (remoteConfig.releaseNotes) {
      const notesLabel = te('appUpdate.releaseNotes') ? t('appUpdate.releaseNotes') : 'Novidades:';
      message += `\n\n${notesLabel}\n${remoteConfig.releaseNotes}`;
    }

    const confirmText = te('appUpdate.updateNow') ? t('appUpdate.updateNow') : 'Atualizar Agora';
    const cancelText = te('appUpdate.later') ? t('appUpdate.later') : 'Mais Tarde';

    isDialogOpen = true;

    if (mustForceUpdate) {
      // Mandatory update: blocking alert dialog with single action button
      await dialogStore.dispatch('alert', {
        title,
        message,
        confirmText
      });
      isDialogOpen = false;
      await redirectToStore(remoteConfig.urls);
    } else {
      // Optional update: confirm dialog with accept / dismiss
      const confirmed = await dialogStore.dispatch('confirm', {
        title,
        message,
        confirmText,
        cancelText
      });
      isDialogOpen = false;

      if (confirmed) {
        await redirectToStore(remoteConfig.urls);
      } else {
        sessionStorage.setItem(dismissedKey, latestVersion);
      }
    }

    return {
      checked: true,
      hasUpdate: true,
      forced: mustForceUpdate,
      currentVersion,
      latestVersion
    };
  } catch (err) {
    console.warn('[AppUpdate] Check failed (network/offline):', err);
    return { checked: false, error: err };
  } finally {
    isChecking = false;
  }
}

/**
 * Initializes listeners to check for updates on resume, window focus, and periodically.
 */
export function initAppUpdateListeners() {
  if (isNativePlatform()) {
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        checkForAppUpdate().catch((e) => console.warn('[AppUpdate] Resume check error:', e));
      }
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => {
      checkForAppUpdate().catch(() => {});
    });
  }

  // Periodic check every 10 minutes while app stays open
  if (!periodicTimer) {
    periodicTimer = setInterval(() => {
      checkForAppUpdate().catch(() => {});
    }, 10 * 60 * 1000);
  }
}

// Expose helper on window for manual check or debugging in console
if (typeof window !== 'undefined') {
  window.__checkForAppUpdate = checkForAppUpdate;
}
